/**
 * Presentation-only mapping from dispatcher FX methods onto particle-world
 * burst params. Shared by Demolition and CottageTown. Never writes physics.
 *
 * Dispatcher → spawn:
 *   spawnImpactBurst     → spawnImpact     (contact puff)
 *   spawnShockwave       → spawnDetonation (blast / concussion)
 *   spawnPulverizedCloud → spawnResidue    (airborne fines)
 */
import type { DemolitionWeapon } from '../../services/damageModel';
import type { ProfileId } from '../../services/destructionProfiles';
import type { DemolitionFxController } from '../fallback/ImpactFx';
import {
  GPU_DUST_HIGH_CAPACITY,
  gpuDustCapacityForTier,
  type DemolitionEffectTier,
} from '../presentation/effectTier';
import { planChipBurst, planChunkBurst, plannedChunkCount, type ChunkSpawnRecord } from './chunkSim';
import type { CloudSpawnRecord } from './cloudSpawn';
import { planImpactExtras, type ImpactParams } from './impactRegime';
import { splitBurstMass } from './massSplit';
import { getProfile, type ParticleMaterialProfile } from './profiles';
import {
  chipCapacityForTier,
  chunkCapacityForTier,
  planBurst,
  plannedBurstCount,
  plannedChipCount,
  type GrainSpawnRecord,
} from './spawn';
import { asNormalTriple, asTriple, type BoxCollider, type BurstParams, type ParticleTier } from './types';

export const DEMOLITION_PARTICLE_ARENA = {
  minX: -8,
  maxX: 8,
  minZ: -6,
  maxZ: 8,
} as const;

/**
 * One shared heightfield covering inspection (offset 0) and town mode
 * (up to three cottages at ±14 m). Particle chunks/grains/fines stay at
 * the demolition overlay capacities (all zero).
 */
export const COTTAGE_TOWN_PARTICLE_ARENA = {
  minX: -24,
  maxX: 24,
  minZ: -12,
  maxZ: 12,
} as const;

/** CottageTown mounts no wall AABB; Box3D pieces own solids. */
export const COTTAGE_TOWN_PARTICLE_COLLIDERS: readonly BoxCollider[] = [];

/**
 * Overlay grain budget. Same role as `chunkCapacityForTier('mobile')` for
 * chunks: `0` skips the GPGPU GRAINS Points system at world mount.
 */
export const DEMOLITION_GRAIN_CAPACITY = 0;

/**
 * Overlay fines / cloudSim budget (`particle-lab-fines`). `0` skips the
 * 4096-point fines mesh at world mount — same pattern as grains/chunks.
 * Airborne look is ImpactFx cloud sprites; leftover fine mass stays on the
 * deposit ledger without extra heightfield stamps.
 */
export const DEMOLITION_FINES_CAPACITY = 0;

/**
 * Overlay pool knobs passed to `createParticleWorld` from
 * DemolitionParticleLayer. Chunk/chip follow the mobile budget (0) at every
 * demolition tier; grains and fines are overlay zeros.
 */
export function demolitionParticleWorldCapacities(): {
  readonly chunkCapacity: number;
  readonly chipCapacity: number;
  readonly grainCapacity: number;
  readonly fineCapacity: number;
} {
  return {
    chunkCapacity: chunkCapacityForTier('mobile'),
    chipCapacity: chipCapacityForTier('mobile'),
    grainCapacity: DEMOLITION_GRAIN_CAPACITY,
    fineCapacity: DEMOLITION_FINES_CAPACITY,
  };
}

/**
 * Share of former grain mass folded into the fine-mass ledger. With
 * `DEMOLITION_FINES_CAPACITY` at 0 those points never enqueue; the rest
 * stays on `splitBurstMass.grainMass` so fate totals still sum without
 * extra heightfield stamps that would change the approved settle.
 */
export const DEMOLITION_GRAIN_MASS_TO_FINES = 0.25;

/** Shockwave radius below this is a concussion puff (matches ImpactFx). */
export const DEMOLITION_CONCUSSION_RADIUS = 2.05;

const CONTACT_MASS: Record<DemolitionWeapon, number> = {
  cannon: 0.1,
  spike: 0.06,
  rubble: 0.14,
  concussion: 0.05,
  bomb: 0.12,
};

export function particleTierForDemolitionEffect(
  tier: DemolitionEffectTier,
): ParticleTier | null {
  if (tier === 'off') return null;
  if (tier === 'high') return 'desktop';
  return 'mobile';
}

/** Scale spawn mass by gpu-dust capacity ratio (basic 4096 / high 16384 = 0.25). */
export function demolitionParticleMassScale(tier: DemolitionEffectTier): number {
  if (tier === 'off') return 0;
  return gpuDustCapacityForTier(tier) / GPU_DUST_HIGH_CAPACITY;
}

/**
 * Grit quad world size (unstretched, demolition high / desktop spriteScale 1).
 *
 * Size chain, matching spawn.ts + RENDER_VERT grit:
 *   sizeScale    = max(0.02, gritSizeScale)          // spawn.ts floor
 *   spawnRadius  = grainSizeRange * sizeScale        // written to meta.x
 *   shaderR      = max(spawnRadius * 2.2, 0.0016)
 *                  * max(uSpriteScale, 0.15)
 *   worldDiameter = shaderR * 2                      // PlaneGeometry ±0.5
 *
 * Uncapped concrete (gritSizeScale 1, grain 20 mm): 20 mm × 4.4 = 88 mm.
 * Stone (30 mm): 132 mm. Motion stretch on the grit billboard then reads
 * ~15–25 cm at demolition camera. Cap sizeScale so unstretched diameter
 * honors grainSizeRange and stays ≤ 3 cm.
 */
export const DEMOLITION_GRIT_SPAWN_SCALE_FLOOR = 0.02;
export const DEMOLITION_GRIT_SHADER_RADIUS_MUL = 2.2;
export const DEMOLITION_GRIT_SHADER_RADIUS_FLOOR = 0.0016;
export const DEMOLITION_GRIT_SPRITE_SCALE_FLOOR = 0.15;
export const DEMOLITION_GRIT_QUAD_SPAN_MUL = 2;
export const DEMOLITION_GRIT_DIAMETER_MUL =
  DEMOLITION_GRIT_SHADER_RADIUS_MUL * DEMOLITION_GRIT_QUAD_SPAN_MUL;
export const DEMOLITION_GRIT_MAX_WORLD_DIAMETER = 0.03;
/**
 * 1 / 4.4 overflows 0.03 m by 1 ulp on stone's 30 mm grain; stay strictly under.
 * Must stay ≥ spawn.ts floor (0.02) or the floor would re-inflate the quad.
 */
export const DEMOLITION_GRIT_SIZE_SCALE_CAP = 0.227;
/** Non-grit grains use an 18 mm visual floor — force the grit path instead. */
const DEMOLITION_GRIT_MAX_LIFE = 1.2;
/** Desktop / demolition-high spriteScale. Mobile uses 0.42 (smaller). */
const DEMOLITION_GRIT_SPRITE_SCALE = 1;

function demolitionFineFraction(profile: ParticleMaterialProfile): number {
  const fine = Math.min(1, Math.max(0, profile.fineFraction));
  return Math.min(1, fine + (1 - fine) * DEMOLITION_GRAIN_MASS_TO_FINES);
}

/** Unstretched grit-quad world diameter (m) from a spawn.ts `radius` (already scaled). */
export function demolitionGritQuadWorldDiameterFromSpawnRadius(
  spawnRadius: number,
  spriteScale: number = DEMOLITION_GRIT_SPRITE_SCALE,
): number {
  const shaderRadius = Math.max(
    spawnRadius * DEMOLITION_GRIT_SHADER_RADIUS_MUL,
    DEMOLITION_GRIT_SHADER_RADIUS_FLOOR,
  ) * Math.max(spriteScale, DEMOLITION_GRIT_SPRITE_SCALE_FLOOR);
  return shaderRadius * DEMOLITION_GRIT_QUAD_SPAN_MUL;
}

/** Unstretched grit-quad world diameter (m) from profile grain size + gritSizeScale. */
export function demolitionGritQuadWorldDiameter(
  grainSizeM: number,
  gritSizeScale: number,
  spriteScale: number = DEMOLITION_GRIT_SPRITE_SCALE,
): number {
  const sizeScale = Math.max(DEMOLITION_GRIT_SPAWN_SCALE_FLOOR, gritSizeScale);
  return demolitionGritQuadWorldDiameterFromSpawnRadius(grainSizeM * sizeScale, spriteScale);
}

function demolitionSignature(
  profile: ParticleMaterialProfile,
): ParticleMaterialProfile['signature'] {
  const sig = profile.signature;
  const gritMaxLife = sig.gritMaxLife > 0 && sig.gritMaxLife < 1.5
    ? sig.gritMaxLife
    : DEMOLITION_GRIT_MAX_LIFE;
  const gritSizeScale = Math.min(sig.gritSizeScale, DEMOLITION_GRIT_SIZE_SCALE_CAP);
  return {
    ...sig,
    chunkFraction: 0,
    chunkCountRange: [0, 0],
    chunkCap: 0,
    chunkStragglerRange: [0, 0],
    chipFraction: 0,
    chipCap: 0,
    cornerChipChance: 0,
    gritFraction: 0,
    gritSizeScale,
    gritMaxLife,
  };
}

/**
 * Demolition overlay profile: shared ParticleLab/SiegeLab catalogs stay
 * untouched. Chunks/chips/grains are zeroed here so Box3D hulls own every
 * solid and airborne grit never mounts. Former grain mass is split onto
 * the fine-mass ledger vs deposit ledger; airborne fine-points are skipped
 * at world mount (`DEMOLITION_FINES_CAPACITY` 0).
 */
export function demolitionParticleProfile(material: ProfileId): ParticleMaterialProfile {
  const profile = getProfile(material);
  return {
    ...profile,
    grainsPerKg: 0,
    fineFraction: demolitionFineFraction(profile),
    signature: demolitionSignature(profile),
  };
}

/**
 * Shockwave / detonation overlay: grit quads spawned at the missile
 * shock-ring radius read as floating squares meters from the wall;
 * gritFraction 0 drops that population. fineFraction 1 folds former grain
 * mass onto the fine-mass ledger (airborne fine-points still skipped at
 * world mount). ImpactFx cloud sprites carry the airborne blast.
 */
function demolitionDetonationProfile(material: ProfileId): ParticleMaterialProfile {
  const profile = demolitionParticleProfile(material);
  return {
    ...profile,
    fineFraction: 1,
    signature: {
      ...profile.signature,
      gritFraction: 0,
    },
  };
}

/** Heightfield / powder-overlay residue colour — profile pileTint, linear 0..1. */
export function demolitionResidueTint(
  material: ProfileId,
): readonly [number, number, number] {
  return demolitionParticleProfile(material).pileTint;
}

function clampEnergy(energy: number): number {
  if (!Number.isFinite(energy)) return 1;
  return Math.min(5, Math.max(0.05, energy));
}

function sprayDirection(nx: number, ny: number, nz: number): [number, number, number] {
  const x = nx * 0.45;
  const y = 0.55 + Math.max(0, ny) * 0.3;
  const z = nz * 0.45;
  const len = Math.hypot(x, y, z) || 1;
  return [x / len, y / len, z / len];
}

function impactForWeapon(weapon: DemolitionWeapon): ImpactParams {
  if (weapon === 'spike') return { kind: 'bullet' };
  if (weapon === 'bomb') return { kind: 'missile' };
  if (weapon === 'concussion') return { kind: 'missile', intensity: 0.35, blastFraction: 0.55 };
  if (weapon === 'rubble') return { kind: 'cannon', intensity: 0.18, blastFraction: 0.08 };
  return { kind: 'cannon' };
}

export interface DemolitionParticleImpactInput {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly radius: number;
  readonly normal: { readonly x: number; readonly y: number; readonly z: number };
  readonly weapon: DemolitionWeapon;
  readonly material: ProfileId;
}

export interface DemolitionParticleDetonationInput {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly radius: number;
  readonly material: ProfileId;
}

export interface DemolitionParticleResidueInput {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly normal: { readonly x: number; readonly y: number; readonly z: number };
  readonly volume: number;
  readonly material: ProfileId;
}

export function burstFromImpact(
  input: DemolitionParticleImpactInput,
  massScale: number,
): BurstParams | null {
  if (!(massScale > 0)) return null;
  const n = input.normal;
  const mass = CONTACT_MASS[input.weapon] * massScale;
  if (!(mass > 1e-8)) return null;
  const energy = clampEnergy(0.45 + input.radius * 3.6);
  return {
    position: [input.x, input.y, input.z],
    direction: sprayDirection(n.x, n.y, n.z),
    energy,
    mass,
    profile: demolitionParticleProfile(input.material),
    surfaceNormal: [n.x, n.y, n.z],
    impact: impactForWeapon(input.weapon),
  };
}

export function burstFromDetonation(
  input: DemolitionParticleDetonationInput,
  massScale: number,
): BurstParams | null {
  if (!(massScale > 0)) return null;
  const concussion = input.radius < DEMOLITION_CONCUSSION_RADIUS;
  const mass = (concussion ? 0.08 : 0.28 * Math.min(1.4, input.radius / 3)) * massScale;
  if (!(mass > 1e-8)) return null;
  return {
    position: [input.x, input.y, input.z],
    direction: [0, 1, 0],
    energy: clampEnergy(concussion ? 0.7 : 2.2),
    mass,
    profile: demolitionDetonationProfile(input.material),
    surfaceNormal: [0, 1, 0],
    impact: concussion
      ? { kind: 'missile', intensity: 0.35, blastFraction: 0.55 }
      : { kind: 'missile' },
  };
}

export function burstFromResidue(
  input: DemolitionParticleResidueInput,
  massScale: number,
): BurstParams | null {
  if (!(massScale > 0) || !(input.volume > 1e-8)) return null;
  const n = input.normal;
  const mass = input.volume * massScale;
  if (!(mass > 1e-8)) return null;
  return {
    position: [input.x, input.y, input.z],
    direction: sprayDirection(n.x, n.y, n.z),
    energy: clampEnergy(0.5 + input.volume * 8),
    mass,
    profile: demolitionParticleProfile(input.material),
    surfaceNormal: [n.x, n.y, n.z],
    impact: { kind: 'cannon', intensity: 0.22, blastFraction: 0.2 },
  };
}

export interface DemolitionParticleController {
  spawnImpact(input: DemolitionParticleImpactInput): void;
  spawnDetonation(input: DemolitionParticleDetonationInput): void;
  spawnResidue(input: DemolitionParticleResidueInput): void;
  reset(): void;
}

/**
 * Fan dispatcher FX calls onto the GPGPU particle controller without
 * replacing ImpactFx. Null sinks drop that half of the pair.
 */
export function overlayPresentationParticles(
  fx: DemolitionFxController | null,
  particles: DemolitionParticleController | null,
): DemolitionFxController | null {
  if (!fx && !particles) return null;
  return {
    spawnShards(shards) {
      fx?.spawnShards(shards);
    },
    spawnShockwave(x, y, z, radius, sequence, material) {
      fx?.spawnShockwave(x, y, z, radius, sequence, material);
      particles?.spawnDetonation({ x, y, z, radius, material });
    },
    spawnImpactBurst(burst, sequence) {
      fx?.spawnImpactBurst(burst, sequence);
      particles?.spawnImpact({
        x: burst.x,
        y: burst.y,
        z: burst.z,
        radius: burst.radius,
        normal: burst.normal,
        weapon: burst.weapon,
        material: burst.material,
      });
    },
    spawnLandingPuff(x, y, z, sequence, material) {
      fx?.spawnLandingPuff(x, y, z, sequence, material);
    },
    spawnTraceReleases(payload, sequence) {
      fx?.spawnTraceReleases(payload, sequence);
    },
    spawnPulverizedCloud(x, y, z, normal, volume, sequence, material) {
      fx?.spawnPulverizedCloud(x, y, z, normal, volume, sequence, material);
      particles?.spawnResidue({ x, y, z, normal, volume, material });
    },
    liveCount() {
      return fx?.liveCount() ?? 0;
    },
    warm() {
      fx?.warm();
    },
  };
}

export interface DemolitionChunkSimPlan {
  readonly chunks: readonly ChunkSpawnRecord[];
  readonly chips: readonly ChunkSpawnRecord[];
  readonly grains: readonly GrainSpawnRecord[];
  readonly fines: readonly CloudSpawnRecord[];
}

/**
 * Spawn-plan that would reach grainSim.enqueue / chunkSim.enqueue /
 * enqueueChips / cloud.enqueue for a demolition overlay burst. Matches
 * world.fireOne's grain + chunk/chip planners without touching the GPU.
 * Overlay capacities of 0 skip the matching population — fines are always
 * empty because `DEMOLITION_FINES_CAPACITY` is 0 at world mount.
 */
export function planDemolitionChunkSimQueue(
  params: BurstParams,
  colliders: readonly BoxCollider[] = [],
  burstIndex = 0,
): DemolitionChunkSimPlan {
  const energy = clampEnergy(params.energy);
  const impact = params.impact;
  const split = splitBurstMass(params.mass, params.profile, impact);
  const position = asTriple(params.position);
  const direction = asTriple(params.direction);
  const surfaceNormal = asNormalTriple(params.surfaceNormal);
  const grainWanted = plannedBurstCount(params.mass, params.profile, 1e9, energy, impact);
  const grains = grainWanted > 0
    ? planBurst({
      position,
      direction,
      energy,
      mass: params.mass,
      profile: params.profile,
      burstIndex,
      surfaceNormal,
      impact,
    })
    : [];
  const chunkWanted = plannedChunkCount(energy, params.profile, 1e9, impact);
  const chunks = split.chunkMass > 0 && chunkWanted > 0
    ? planChunkBurst({
      position,
      direction,
      energy,
      chunkMass: split.chunkMass,
      profile: params.profile,
      burstIndex,
      surfaceNormal,
      impact,
    })
    : [];
  const chipWanted = plannedChipCount(params.mass, params.profile, 1e9, energy, impact);
  const chips = chipWanted > 0
    ? planChipBurst({
      position,
      direction,
      energy,
      mass: params.mass,
      chipMass: split.grainMass * params.profile.signature.chipFraction,
      profile: params.profile,
      burstIndex,
      surfaceNormal,
      impact,
    })
    : [];
  const extras = planImpactExtras({
    position,
    energy,
    profile: params.profile,
    burstIndex,
    surfaceNormal,
    impact,
    colliders,
  });
  // Overlay fineCapacity is 0: world never creates cloudSim, so no
  // planCloudBurst / scheduledFines / extras.fines enqueue.
  const fines: CloudSpawnRecord[] = [];
  return { chunks, chips: chips.concat(extras.chips), grains, fines };
}
